import React from "react";
import * as R from "ramda";
import { Modal, notification } from "antd";
import { makeAction } from "async-action-creator";

import { createApiThunk } from "../../utils/thunk";
import {
  getCandidateTypes,
  getBatchExternalFails,
  getBatchInternalFails,
  makeBatchApiRequest,
  retryBatchModal,
  retryBatchInvite
} from "../../utils/batchActions";
import { listHasItems } from "../../utils/arrays";
import {
  BATCH_ACTIONS,
  CANDIDATE_TYPES,
  DEFAULT_PROCESS_STEPS,
  PROCESS_BATCH_ACTIONS,
  PROCESS_BATCH_ACTIONS_TRANSLATIONS
} from "../../constants/opportunity";
import { idInList } from "../../utils/candidates";
import api from "../../utils/api";
import { pluralsForList } from "../../utils/strings";
import i18n from "../../i18n";

export const PROCESS_BATCH_ACTION = makeAction(
  "procedure/PROCESS_BATCH_ACTION"
);

export const PROCESS_BATCH_INVITE = makeAction(
  "procedure/PROCESS_BATCH_INVITE"
);

export const RESET_SELECTED_CANDIDATES = makeAction(
  "process/RESET_SELECTED_CANDIDATES"
);
export const RESET_FILTERS = makeAction("process/RESET_FILTERS");

export const SELECT_CANDIDATE = makeAction("process/SELECT_CANDIDATE");
export const SELECT_CANDIDATES = makeAction("process/SELECT_CANDIDATES");

export const SET_STEP_FILTERS = makeAction("process/SET_STEP_FILTERS");

export const resetSelectedCandidates = RESET_SELECTED_CANDIDATES.run;
export const resetFilters = RESET_FILTERS.run;

export const selectCandidate = SELECT_CANDIDATE.run;
export const selectAllCandidates = SELECT_CANDIDATES.run;

export const setStepFilters = SET_STEP_FILTERS.run;

export const processBatchAction = createApiThunk({
  action: PROCESS_BATCH_ACTION,
  request: async (payload, { company, opportunity }, dispatch) => {
    dispatch(resetSelectedCandidates());

    const {
      action,
      candidates,
      step = null,
      nextStep = null,
      getContext
    } = payload;

    const idCompany = company.id;
    const idOpportunity = opportunity.selected.id;

    const { externalCandidates, internalCandidates } = getCandidateTypes(
      candidates
    );

    let actionPayload;

    if (action === BATCH_ACTIONS.process.moveToStep) {
      const { dispatch: processDispatch } = getContext();

      processDispatch({
        type: "SET_MATCHES_LOADING",
        payload: {
          step,
          loading: true,
          matches: candidates
        }
      });

      actionPayload = {
        step: R.prop("id", nextStep)
      };
    }

    const makeProcessBatchApiRequest = (candidatesList, type) =>
      makeBatchApiRequest({
        action,
        actions: PROCESS_BATCH_ACTIONS,
        candidates: candidatesList,
        idCompany,
        idOpportunity,
        payload: actionPayload,
        step: nextStep,
        type
      });

    const requests = R.filter(Boolean, [
      makeProcessBatchApiRequest(
        externalCandidates,
        CANDIDATE_TYPES.external.type
      ),
      makeProcessBatchApiRequest(
        internalCandidates,
        CANDIDATE_TYPES.internal.type
      )
    ]);

    const responses = await Promise.all(requests);

    return { payload, responses };
  },
  onResolved: async ({ response, getState, dispatch }) => {
    const { payload, responses } = response;

    const { action, candidates, step, nextStep = null, getContext } = payload;

    const { externalCandidates, internalCandidates } = getCandidateTypes(
      candidates
    );

    const [externalResponse, internalResponse] = responses;

    const {
      dispatch: processDispatch,
      state: processState,
      updateStepColumnOrder
    } = getContext();

    const candidateIds = R.map(R.prop("id"), candidates);

    const failedIds = R.flatten([
      getBatchExternalFails(externalResponse),
      getBatchInternalFails(internalResponse)
    ]);

    // If there's some candidates that succeeded, dispatch actions to the context
    if (R.not(R.eqBy(R.length, candidateIds, failedIds))) {
      switch (action) {
        case BATCH_ACTIONS.process.moveToRejected: {
          const updatedSteps = R.evolve({
            [step.id]: {
              matches: R.reject(idInList(R.difference(candidateIds, failedIds)))
            }
          })(processState.steps);

          processDispatch({
            type: "UPDATE_STEPS_STARTED",
            payload: updatedSteps
          });

          try {
            const { opportunity, company } = getState();
            function readChat(candidateId) {
              return api.messages.read(
                opportunity.selected.id,
                candidateId,
                company.id
              );
            }
            const internalCandidateIds = R.map(
              R.prop("id"),
              internalCandidates
            );
            await Promise.all(
              R.map(readChat, R.difference(internalCandidateIds, failedIds))
            );
            await updateStepColumnOrder(updatedSteps[step.id]);
            processDispatch({ type: "UPDATE_STEPS_RESOLVED" });
          } catch (error) {
            processDispatch({ type: "UPDATE_STEPS_REJECTED" });
            // show error notification?
          }
          break;
        }

        case BATCH_ACTIONS.process.moveToStep: {
          const updatedSteps = R.evolve({
            [step.id]: {
              matches: R.reject(idInList(R.difference(candidateIds, failedIds)))
            },
            [nextStep.id]: {
              matches: R.concat(
                R.filter(
                  idInList(R.difference(candidateIds, failedIds)),
                  candidates
                )
              )
            }
          })(processState.steps);

          processDispatch({
            type: "SET_MATCHES_LOADING",
            payload: {
              step,
              loading: false,
              matches: candidates
            }
          });

          const externalCandidatesThatSucceeded = R.difference(
            R.map(R.prop("id"), externalCandidates),
            failedIds
          );

          processDispatch({
            type: "UPDATE_STEPS_STARTED",
            payload: updatedSteps
          });

          try {
            if (R.length(externalCandidatesThatSucceeded)) {
              const candidatesToInvite = R.filter(
                idInList(externalCandidatesThatSucceeded),
                externalCandidates
              );

              const plural = pluralsForList(candidatesToInvite);
              Modal.confirm({
                title: i18n.t("global:alert"),
                content: (
                  <>
                    <p>
                      {i18n.t(
                        `opportunity:candidates.multipleSelection.${
                          plural
                            ? "inviteCandidatesModalContent1"
                            : "inviteCandidateModalContent1"
                        }`
                      )}
                    </p>
                    <p>
                      {i18n.t(
                        `opportunity:candidates.multipleSelection.${
                          plural
                            ? "inviteCandidatesModalContent2"
                            : "inviteCandidateModalContent2"
                        }`
                      )}
                    </p>
                    <p>
                      {i18n.t(
                        `opportunity:candidates.multipleSelection.${
                          plural
                            ? "inviteCandidatesModalContent3"
                            : "inviteCandidateModalContent3"
                        }`
                      )}
                    </p>
                  </>
                ),

                okButtonProps: {
                  "data-test": "multiple-email-invite-modal-button-ok"
                },
                cancelButtonProps: {
                  "data-test": "multiple-email-invite-modal-button-cancel"
                },
                okText: "Enviar invitación",
                cancelText: "Continuar manualmente",
                autoFocusButton: "cancel",
                onOk: () => {
                  dispatch(
                    processBatchInvite({
                      getContext: payload.getContext,
                      action: BATCH_ACTIONS.process.sendInvite,
                      step: nextStep,
                      candidates: candidatesToInvite
                    })
                  );
                }
              });
            }

            await Promise.all([
              updateStepColumnOrder(updatedSteps[step.id]),
              updateStepColumnOrder(updatedSteps[nextStep.id])
            ]);
            processDispatch({ type: "UPDATE_STEPS_RESOLVED" });
          } catch (error) {
            processDispatch({ type: "UPDATE_STEPS_REJECTED" });
            // show error notification?
          }
          break;
        }

        default: {
        }
      }
    }

    if (listHasItems(failedIds)) {
      if (R.equals(action, BATCH_ACTIONS.process.moveToStep)) {
        processDispatch({
          type: "SET_MATCHES_LOADING",
          payload: {
            step,
            loading: false,
            matches: candidates
          }
        });
      }
      const single = R.not(pluralsForList(candidates));
      const plural = pluralsForList(failedIds);

      retryBatchModal({
        action,
        actions: PROCESS_BATCH_ACTIONS_TRANSLATIONS,
        allFailed: R.and(
          R.not(single),
          R.eqBy(R.length, failedIds, candidates)
        ),
        candidates,
        failedIds,
        plural,
        single,
        place: i18n.t("opportunity:candidates.multipleSelection.originStep", {
          step: R.includes(R.prop("name", step), R.keys(DEFAULT_PROCESS_STEPS))
            ? i18n.t(`opportunity:process.defaultSteps.${R.prop("name", step)}`)
            : `${step.name[0].toUpperCase()}${step.name.slice(1)}`
        }),
        retryCallback: p => dispatch(processBatchAction({ ...payload, ...p })),
        translationOptions: { step: nextStep && nextStep.name }
      });
    } else {
      notification.success({
        message: i18n.t("global:good"),
        description: i18n.t(
          `opportunity:candidates.multipleSelection.${
            pluralsForList(candidates)
              ? R.equals(action, BATCH_ACTIONS.process.sendInvite)
                ? "invitedCandidates"
                : "movedCandidatesTo"
              : R.equals(action, BATCH_ACTIONS.process.sendInvite)
              ? "invitedCandidate"
              : "movedCandidateTo"
          }`,
          {
            count: R.length(candidates),
            action: PROCESS_BATCH_ACTIONS_TRANSLATIONS[action]({
              step: nextStep
                ? R.includes(nextStep.name, [
                    DEFAULT_PROCESS_STEPS.selected,
                    DEFAULT_PROCESS_STEPS.accepted
                  ])
                  ? i18n.t(
                      `opportunity:process.defaultSteps.${R.prop(
                        "name",
                        nextStep
                      )}`
                    )
                  : nextStep.name
                : // TODO revise this case
                  ""
            })
          }
        )
      });
    }
  },
  onRejected: ({ payload, dispatch }) => {
    const { action, candidates, step, nextStep = null, getContext } = payload;

    if (action === BATCH_ACTIONS.process.moveToStep) {
      const { dispatch: processDispatch } = getContext();

      processDispatch({
        type: "SET_MATCHES_LOADING",
        payload: {
          step,
          loading: false,
          matches: candidates
        }
      });
    }

    retryBatchModal({
      action,
      actions: PROCESS_BATCH_ACTIONS_TRANSLATIONS,
      allFailed: pluralsForList(candidates),
      candidates,
      failedIds: R.map(R.prop("id"), candidates),
      plural: pluralsForList(candidates),
      single: R.not(pluralsForList(candidates)),
      place: i18n.t("opportunity:candidates.multipleSelection.originStep", {
        step: R.includes(step.name, R.keys(DEFAULT_PROCESS_STEPS))
          ? i18n.t(`opportunity:process.defaultSteps.${R.prop("name", step)}`)
          : `${step.name[0].toUpperCase()}${step.name.slice(1)}`
      }),
      retryCallback: p => dispatch(processBatchAction({ ...payload, ...p })),
      translationOptions: { step: nextStep && nextStep.name }
    });
  },
  passPayloadToError: true
});

export const processBatchInvite = createApiThunk({
  action: PROCESS_BATCH_INVITE,
  request: async (payload, { company, opportunity }, dispatch) => {
    dispatch(resetSelectedCandidates());

    const { action, candidates, getContext, step } = payload;

    const idCompany = company.id;
    const idOpportunity = opportunity.selected.id;

    const { dispatch: processDispatch, setMatchesInviteLoading } = getContext();

    processDispatch(setMatchesInviteLoading(step.id, candidates, "loading"));

    const response = await makeBatchApiRequest({
      action,
      actions: PROCESS_BATCH_ACTIONS,
      candidates,
      emptyBody: true,
      idCompany,
      idOpportunity,
      type: CANDIDATE_TYPES.external.type
    });

    return { payload, response };
  },
  onResolved: async ({ response: { payload, response }, dispatch }) => {
    const { action, candidates, getContext, step } = payload;

    const { dispatch: processDispatch, setMatchesInviteLoading } = getContext();

    processDispatch(setMatchesInviteLoading(step.id, candidates, "resolved"));

    const failedIds = getBatchExternalFails(response);

    if (listHasItems(failedIds)) {
      const single = R.not(pluralsForList(candidates));
      const plural = pluralsForList(failedIds);

      retryBatchInvite({
        action,
        actions: PROCESS_BATCH_ACTIONS_TRANSLATIONS,
        allFailed: R.and(
          R.not(single),
          R.eqBy(R.length, failedIds, candidates)
        ),
        candidates,
        failedIds,
        plural,
        retryCallback: p => dispatch(processBatchInvite({ ...payload, ...p })),
        single
      });
    } else {
      notification.success({
        message: i18n.t("global:good"),
        description: i18n.t(
          `opportunity:candidates.multipleSelection.${
            pluralsForList(candidates)
              ? "invitedCandidates"
              : "invitedCandidate"
          }`,
          {
            count: R.length(candidates),
            action: PROCESS_BATCH_ACTIONS_TRANSLATIONS[action]()
          }
        )
      });
    }
  },
  onRejected: ({ payload, dispatch }) => {
    const { action, candidates, getContext, step } = payload;

    const { dispatch: processDispatch, setMatchesInviteLoading } = getContext();

    processDispatch(setMatchesInviteLoading(step.id, candidates, "rejected"));

    retryBatchInvite({
      action,
      actions: PROCESS_BATCH_ACTIONS_TRANSLATIONS,
      allFailed: true,
      candidates,
      dispatch,
      failedIds: R.map(R.prop("id"), candidates),
      plural: pluralsForList(candidates),
      single: R.not(pluralsForList(candidates)),
      retryCallback: p => dispatch(processBatchInvite({ ...payload, ...p }))
    });
  },
  passPayloadToError: true
});
